import { Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { normVerdict, openGatedLink } from "@/lib/shield";

// Shield verdict → chip styling (solid dark base so the chip stays legible inside bright own bubbles).
const VERDICT_STYLE = {
  blocked: "border-destructive/50 text-destructive",
  warning: "border-amber-500/50 text-amber-400",
  safe: "border-bloq-mint/40 text-bloq-mint",
  unknown: "border-white/15 text-white/80",
};
const VERDICT_TEXT = {
  blocked: "text-destructive",
  warning: "text-amber-400",
  safe: "text-bloq-mint",
  unknown: "text-white/70",
};

// Renders a Shield-scanned link. When the backend has fetched OpenGraph metadata
// (`link.preview`), it upgrades to a Telegram-style rich card; otherwise the compact
// security chip is shown. Every tap goes through the Shield link gate (§3.2l): safe links
// open directly, caution/unknown/blocked show the interstitial first.
export function LinkPreview({ link, testid }) {
  const v = normVerdict(link.verdict);
  const style = VERDICT_STYLE[v];
  const p = link.preview;
  const hasRich = p && (p.title || p.description || p.image_b64);
  const open = (e) => { e.stopPropagation(); openGatedLink(link); };

  if (!hasRich) {
    return (
      <button type="button" onClick={open} className={cn("mt-2 flex w-full items-start gap-2 rounded-xl border bg-bloq-bg/80 px-3 py-2 text-left text-xs transition-colors hover:brightness-110", style)} data-testid={testid}>
        <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="min-w-0">
          <div className="truncate font-mono">{link.url}</div>
          <div className="opacity-90">{link.label}</div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      className="mt-2 block w-full overflow-hidden rounded-xl border border-white/12 bg-bloq-bg/70 text-left transition-colors hover:border-bloq-mint/40"
      data-testid={testid}
    >
      {p.image_b64 && (
        <img
          src={`data:image/jpeg;base64,${p.image_b64}`}
          alt=""
          loading="lazy"
          className="h-36 w-full object-cover"
          data-testid={testid ? `${testid}-image` : undefined}
        />
      )}
      <div className="p-3">
        {p.site_name && <div className="text-[10px] font-bold uppercase tracking-wider text-bloq-muted">{p.site_name}</div>}
        {p.title && <div className="mt-0.5 line-clamp-2 text-sm font-semibold text-white">{p.title}</div>}
        {p.description && <div className="mt-1 line-clamp-2 text-xs leading-relaxed text-bloq-muted">{p.description}</div>}
        <div className={cn("mt-2 flex items-center gap-1.5 border-t border-white/8 pt-2 text-[10px]", VERDICT_TEXT[v])}>
          <Link2 className="h-3 w-3 shrink-0" />
          <span className="truncate font-mono">{link.url}</span>
          <span className="shrink-0 opacity-80">· {link.label}</span>
        </div>
      </div>
    </button>
  );
}
